import {inject, Injector, runInInjectionContext} from '@angular/core';
import {toObservable} from '@angular/core/rxjs-interop';
import {MatSnackBar} from '@angular/material/snack-bar';
import {ActivatedRouteSnapshot, CanActivateFn, Router, UrlTree} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {filter, map, startWith} from 'rxjs/operators';
import {UserStore} from './state/user.store';
import {take} from "rxjs";


function waitForUserLoaded(userStore: UserStore) {
  return toObservable(userStore.currentUser).pipe(
    startWith(userStore.currentUser()),
    filter(user => !!user)
  );
}

/**
 * OR-based role guard with per-role snackbar messages.
 *
 * Waits for the {@link UserStore} to load, evaluates all provided role checks,
 * and allows navigation if at least one check passes. If none pass, shows the
 * snackbar message associated with the roles and redirects to `/`.
 *
 * @param checks Array of tuples `[roleCheckFn, snackbarMessageKey]`
 * @param redirect Optional function to define a custom redirect path
 */
export function userStoreOrGuard(
  checks: Array<[(store: UserStore) => boolean, string | null]>,
  redirect?: (route: ActivatedRouteSnapshot, router: Router) => UrlTree
): CanActivateFn {
  return (route, state) => {
    const injector = inject(Injector);

    return runInInjectionContext(injector, () => {
      const userStore = inject(UserStore);
      const router = inject(Router);
      const snackBar = inject(MatSnackBar);
      const translateService = inject(TranslateService);

      return waitForUserLoaded(userStore).pipe(
        take(1),
        map(() => {
          const hasPassed = checks.some(([checkFn]) => checkFn(userStore));

          if (hasPassed) {
            return true;
          }

          // Handle Snackbar
          const firstFailedMessageKey = checks.find(([_, key]) => !!key)?.[1];
          snackBar.open(
            translateService.instant(firstFailedMessageKey ?? 'permission.unauthorized'),
            undefined,
            { duration: 3000 }
          );

          return redirect
            ? redirect(route, router)
            : router.createUrlTree(['/']);
        })
      );
    });
  };
}
